import React, { createContext, useState, useCallback } from "react";
import { useSelector } from "react-redux";
import { findAll, findAllGroupItems } from "../../services/itemsGroupService";

export const ItemsGroupContext = createContext({
  dataList: null,
  data: null,
  isLoadingDataList: false,
  isDrawerVisible: false,
  groupItems: [],
  isClassifyDrawerVisible: false,
  classifyRecord: null,
  options: [],
  isLoadingOptions: false,
  lastSaved: null,
  drawerDefaults: null,
});

export const ItemsGroupContextProvider = ({ children }) => {
  const [dataList, setDataList] = useState(null);
  const [data, setData] = useState(null);
  const [isLoadingDataList, setIsLoadingDataList] = useState(false);
  const [isDrawerVisible, setIsDrawerVisible] = useState(false);
  const [groupItems, setGroupItems] = useState([]);
  const [isClassifyDrawerVisible, setIsClassifyDrawerVisible] = useState(false);
  const [classifyRecord, setClassifyRecord] = useState(null);
  const [options, setOptions] = useState([]);
  const [isLoadingOptions, setIsLoadingOptions] = useState(false);
  const [lastSaved, setLastSaved] = useState(null);
  const [drawerDefaults, setDrawerDefaults] = useState(null);

  const {
    groupSelected: { id: groupId },
    farmSelected: { id: farmId },
  } = useSelector((state) => state.app);

  const fetchGroupItems = useCallback(async () => {
    try {
      const response = await findAllGroupItems({ groupId, farmId });
      const groupItemsData = response.data.results;
      setGroupItems(
        Array.isArray(groupItemsData)
          ? groupItemsData
          : (groupItemsData?.content ?? []),
      );
    } catch (error) {
    } finally {
    }
  }, [groupId, farmId]);

  const fetchData = useCallback(
    async (pagination = {}) => {
      setIsLoadingDataList(true);
      try {
        const [itemsResponse, groupItemsResponse] = await Promise.all([
          findAll({ groupId, farmId, ...pagination }),
          findAllGroupItems({ groupId, farmId }),
        ]);
        setDataList(itemsResponse.data.results);
        const groupItemsData = groupItemsResponse.data.results;
        setGroupItems(
          Array.isArray(groupItemsData)
            ? groupItemsData
            : (groupItemsData?.content ?? []),
        );
      } catch (error) {
      } finally {
        setIsLoadingDataList(false);
      }
    },
    [groupId, farmId],
  );

  const fetchOptions = useCallback(async () => {
    setIsLoadingOptions(true);
    try {
      const {
        data: { results },
      } = await findAll({ groupId, farmId, size: 1000 });
      setOptions(Array.isArray(results) ? results : (results?.content ?? []));
    } catch (error) {
      setOptions([]);
    } finally {
      setIsLoadingOptions(false);
    }
  }, [groupId, farmId]);

  const notifySaved = useCallback((entity) => {
    setLastSaved(entity);
  }, []);

  const openDrawer = useCallback(
    (record = null, defaults = null) => {
      if (record) setData(record);
      setDrawerDefaults(defaults);
      fetchGroupItems();
      setIsDrawerVisible(true);
    },
    [fetchGroupItems],
  );

  const closeDrawer = useCallback(() => {
    setData(null);
    setDrawerDefaults(null);
    setIsDrawerVisible(false);
  }, []);

  const openClassifyDrawer = useCallback((record) => {
    setClassifyRecord(record);
    setIsClassifyDrawerVisible(true);
  }, []);

  const closeClassifyDrawer = useCallback(() => {
    setClassifyRecord(null);
    setIsClassifyDrawerVisible(false);
  }, []);

  return (
    <ItemsGroupContext.Provider
      value={{
        dataList,
        data,
        isLoadingDataList,
        isDrawerVisible,
        groupItems,
        options,
        isLoadingOptions,
        lastSaved,
        drawerDefaults,
        fetchData,
        fetchOptions,
        notifySaved,
        openDrawer,
        closeDrawer,
        isClassifyDrawerVisible,
        classifyRecord,
        openClassifyDrawer,
        closeClassifyDrawer,
      }}
    >
      {children}
    </ItemsGroupContext.Provider>
  );
};
